// src/ContentBlocks/WorksheetBlock.jsx
// Independent ContentBlock for rendering a Worksheet block.
// Drops in alongside your other ContentBlocks.
//
// Usage in your central renderer (example):
//   import WorksheetBlock from './ContentBlocks/WorksheetBlock';
//   // inside switch(block.type):
//   case 'worksheet':
//     return (
//       <WorksheetBlock
//         block={block}
//         isEditMode={isEditMode}
//         onBlockUpdate={onBlockUpdate}
//         onSaveBlock={onSaveBlock}
//         isStudentView={false}
//       />
//     );
//
// Notes:
// - onBlockUpdate(updated) should replace the current block with `updated` in your section state
// - onSaveBlock(updated) is optional; if provided, it will be called after Save in the editor
// - isStudentView=false renders the read-only preview when edit mode is off
// - Styling relies on the worksheet rules you added to LessonTemplate.css

import React, { memo } from 'react';
import { WorksheetComponent } from '../WorksheetModule';

const noop = () => {};

const WorksheetBlock = memo(function WorksheetBlock({
  block,
  isEditMode = false,
  onBlockUpdate = noop,
  onSaveBlock = noop,
  isStudentView = false,
  onImportJson = noop,
  onPasteJson = noop,
}) {
  if (!block) return null;

  const handleExport = () => {
    try {
      const data = {
        title: block.title,
        instructions: block.description || block.instructions || '',
        footerNote: block.footerNote || '',
        usePoints: block.usePoints,
        questions: block.questions || [],
      };
      if (block.usePoints) {
        data.totalPoints = block.totalPoints;
      }
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${block.title || 'worksheet'}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export worksheet JSON', err);
    }
  };

  return (
    <div className="worksheet-block-wrapper my-6">
      <WorksheetComponent
        worksheet={block}
        isEditMode={isEditMode}
        onUpdate={(updated) => onBlockUpdate({ ...block, ...updated })}
        onSave={(updated) => onSaveBlock({ ...block, ...updated })}
        isStudentView={isStudentView}
      />

      {isEditMode && (
        <>
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              📝 <strong>Worksheet Block:</strong> Students can fill out and print this worksheet.
              Use the editor above to add questions, set points, and tweak options.
            </p>
          </div>
          <div className="no-print mt-3">
            <label className="text-sm font-medium block mb-2">Worksheet JSON</label>
            <div className="flex items-center gap-2">
              <input type="file" accept="application/json" onChange={onImportJson(block.id)} />
              <button
                type="button"
                className="px-2 py-1 text-sm bg-gray-200 rounded"
                onClick={handleExport}
              >
                Export
              </button>
              <button
                type="button"
                className="px-2 py-1 text-sm bg-gray-200 rounded"
                onClick={onPasteJson(block.id)}
              >
                Paste JSON
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Import from a file or paste JSON to load a worksheet.</p>
          </div>
        </>
      )}
    </div>
  );
});

export default WorksheetBlock;
